export const CRITERIA = [
  // ── Voor het gezin essentieel ──────────────────────────────
  { id: "youngFamilies",       label: "Jonge gezinnen aanwezig",         weight: 9, hint: "Internationale/lokale families met kinderen 0–10 jr." },
  { id: "landBudget",           label: "Betaalbare grondprijs",           weight: 7, hint: "Landbouwgrond €5k–€30k/ha voor wellness boerderij binnen budget." },
  { id: "climate",             label: "Mild klimaat (geen >40°C)",       weight: 8, hint: "Atlantisch gemodereerd, weinig hittegolf-dagen." },
  { id: "services",            label: "Scholen & ziekenhuis bereik",     weight: 6, hint: "Afstand tot internationale school + privé/publiek ziekenhuis." },
  // Distance-based: per-town score afgeleid van werkelijke km tot dichtsbijzijnde school.
  { id: "montessori",          label: "Montessori (km)",                 weight: 7, hint: "Afstand tot dichtsbijzijnde Montessori-school (computed)." },
  { id: "forestSchool",        label: "Bos- / natuurschool (km)",        weight: 7, hint: "Afstand tot dichtsbijzijnde forest school (computed)." },

  // ── Klimaat-derived: rechtstreeks uit concelho.climate ─────
  // Score 1-5 wordt automatisch afgeleid uit jaarcijfers (IPMA Normais 1991-2020).
  { id: "dryness",             label: "Droger dan Zwolle (auto)",        weight: 7, hint: "Score gebaseerd op jaarlijkse neerslag t.o.v. Zwolle (827 mm)." },
  { id: "sunshine",            label: "Meer zon dan Zwolle (auto)",      weight: 7, hint: "Score gebaseerd op zonneuren t.o.v. Zwolle (1676h)." },

  // ── Lifestyle ──────────────────────────────────────────────
  { id: "coffee",              label: "Lokale community & café",         weight: 6, hint: "Plekken om elkaar te ontmoeten." },
  { id: "wellness",            label: "Wellness / mindful community",    weight: 6, hint: "Yoga-studio's, mindfulness, holistic — voor jullie." },
  { id: "surf",                label: "Surf-cultuur",                    weight: 6, hint: "Surfscholen + surfen vanaf het strand. Lifestyle-indicator." },

  // ── Slow-living ────────────────────────────────────────────
  { id: "natureAccess",        label: "Toegang tot natuur",              weight: 9, hint: "Natuurparken, bossen, wandelpaden, kustlandschap." },
  { id: "tranquility",         label: "Rust / lage toerist-druk",        weight: 8, hint: "Karakter buiten piekseizoen." },
  { id: "culturalAuthenticity",label: "Cultureel-authentiek",            weight: 7, hint: "Lokale gemeenschap i.t.t. expat-enclaves." },
  { id: "foodAutonomy",        label: "Zelfvoorziening-potentieel",      weight: 8, hint: "Bodem + water + ruimte voor moestuin, boomgaard, dieren." },

  // ── Praktisch: afgeleid uit concelho-data ──────────────────
  { id: "internet",            label: "Snel internet (glasvezel)",       weight: 7, hint: "Glasvezel vs. gemengd — belangrijk voor thuiswerken/remote." },
  { id: "water",               label: "Waterzekerheid",                  weight: 8, hint: "Risico op droogte/watertekort — cruciaal voor irrigatie & homestead." },
];

export const DEFAULT_WEIGHTS = Object.fromEntries(CRITERIA.map(c => [c.id, c.weight]));

// Hard veto limits — strikte criteria die "ja/nee" beslissen i.p.v. een score af te leiden.
// Zwolle-referentie: KNMI Heino 1991-2020 → 827 mm regen/jr, 1676 zonneuren/jr.
export const HARD_LIMITS = {
  seaDistanceMax:        30,
  fireRiskMaxClass:    3,     // klasse 4-5 = harde veto (ICNF)    // minuten rijden naar zee
  heatDays35Max:         25,    // dagen/jr boven 35°C
  summerMaxP95Max:       39,    // 95e percentiel zomertemp in °C
  annualRainfallMaxMm:   827,   // moet droger zijn dan Zwolle
  annualSunshineMinH:    1676,  // moet meer zon hebben dan Zwolle
};

// Auto-derived score buckets — gebruikt door scoring.js voor "dryness" en "sunshine".
// Gespiegeld op de Zwolle-baseline zodat alles boven de hard-veto nog onderscheid maakt.
export const DRYNESS_BUCKETS = [
  { maxMm:  500, score: 5, label: "Extreem droog (zuid-Algarve)" },
  { maxMm:  650, score: 4, label: "Droog (Alentejo-kust)"        },
  { maxMm:  800, score: 3, label: "Gemiddeld (Silver Coast)"     },
  { maxMm: 1000, score: 2, label: "Natter dan Zwolle"            },
  { maxMm: Infinity, score: 1, label: "Veel natter dan Zwolle"   },
];

export const SUNSHINE_BUCKETS = [
  { minH: 3000, score: 5, label: "Zon-paradijs (Algarve)" },
  { minH: 2800, score: 4, label: "Veel zon"               },
  { minH: 2600, score: 3, label: "Ruim boven Zwolle"      },
  { minH: 2400, score: 2, label: "Boven Zwolle"           },
  { minH:    0, score: 1, label: "Grijzer (Costa Verde)"  },
];

export const DEFAULT_SETTINGS = {
  enforceVeto: true,
  retreatBusinessMode: false,
  waterVeto: true,
};

export const VIBE_THEMES = {
  atlantic:       { gradient: "linear-gradient(135deg, #4a7f9c 0%, #8ab8c8 60%, #c8d8c0 100%)", accent: "#3d6e8a" },
  algarve:        { gradient: "linear-gradient(135deg, #c0b070 0%, #a8c898 60%, #5a8268 100%)", accent: "#7a8a48" },
  alentejo:       { gradient: "linear-gradient(135deg, #c8a878 0%, #d4b888 50%, #98a858 100%)", accent: "#a08560" },
  comporta:       { gradient: "linear-gradient(135deg, #e8d4a8 0%, #d4b878 50%, #88a098 100%)", accent: "#c4a868" },
  "silver-coast": { gradient: "linear-gradient(135deg, #b8c8d4 0%, #d4d8c8 50%, #c8a878 100%)", accent: "#7a8898" },
  arrabida:       { gradient: "linear-gradient(135deg, #5a8a98 0%, #98c8a8 60%, #b8c898 100%)", accent: "#4a7888" },
  historic:       { gradient: "linear-gradient(135deg, #a89878 0%, #c8a878 50%, #786850 100%)", accent: "#8a6b4a" },
  port:           { gradient: "linear-gradient(135deg, #687888 0%, #98a8b8 60%, #b8b8a8 100%)", accent: "#586878" },
};
